// maplibre-map.js — MapLibre GL style generator for the City Map Print.
// Renders OpenMapTiles vector tiles (OpenFreeMap planet) into a poster style:
// tinted land / water / parks, soft building footprints, and crisp WHITE roads
// that stay sharp at any zoom (vector overzoom, unlike the raster canvas).
(function () {
  function hx(h) {
    h = (h || "#000").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const h2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  function mix(a, b, t) {
    const A = hx(a), B = hx(b);
    return "#" + h2(A[0] + (B[0] - A[0]) * t) + h2(A[1] + (B[1] - A[1]) * t) + h2(A[2] + (B[2] - A[2]) * t);
  }
  const isWhite = (c) => { c = (c || "").toLowerCase(); return c === "#fff" || c === "#ffffff"; };

  // OMT source. maxzoom 14; MapLibre overzooms geometry crisply past that.
  const OMT_URL = (typeof window !== "undefined" && window.SOUVENIR_OMT_URL) || "https://tiles.openfreemap.org/planet";

  function buildGLStyle(theme, opts) {
    const labelsOn = !!(opts && opts.labels);
    const bg = theme.bg, water = theme.water, park = theme.park;
    const dark = theme.text || "#2a2722";
    const labelColor = theme.mapLabelColor || theme.labelColor || dark;
    // roads white on colored land; if a theme kept dark roads (Black & White),
    // honour its own road colour instead.
    const road = isWhite(theme.minor) ? "#ffffff" : (theme.minor || "#ffffff");
    const roadWhite = isWhite(theme.minor);
    // major/highway roads can carry their own accent colour (mapbuilder look:
    // white minor streets, coloured arterials); fall back to the minor colour.
    const majC = theme.major || road;
    const hwyC = theme.highway || majC;
    // building + road casing are subtle tints derived from the land so the map
    // reads as one palette. On dark themes buildings lift slightly; on light
    // themes they deepen slightly.
    const deepBg = mix(bg, dark, 0.5);
    const building = theme.building || mix(bg, dark, 0.09);
    const buildingStroke = theme.buildingStroke || null;
    const casing = theme.casing || mix(bg, dark, roadWhite ? 0.16 : 0.0);
    const hwyCasing = theme.highwayCasing || null;
    const rail = theme.rail || mix(bg, dark, 0.28);
    const naturalOp = (theme.naturalOpacity != null) ? theme.naturalOpacity : 0.55;

    // DETAIL LOCK: evaluate every zoom-keyed width / size / opacity at ONE fixed
    // reference zoom (the richest close-up look) so the map shows the SAME level
    // of detail — road weights, textures, and the full label set — at every zoom
    // the customer can use, not just at the closest one. Using a constant input
    // freezes the zoom curve while keeping per-feature (class) matches intact.
    // It also removes the old zoom-offset expression that could blank the export.
    const DETAIL_Z = (typeof window !== "undefined" && window.SOUVENIR_DETAIL_Z) || 13;
    const ZIN = DETAIL_Z;
    // PRINT SCALE: the paper is far bigger than the on-screen preview (an A4 at
    // 300 DPI is ~2480px wide vs ~500px on screen), so the print is rendered at
    // print pixels from DEEPER tiles — real extra detail, not an upscale. Stroke
    // weights and label sizes are multiplied by that same scale so the poster
    // reads exactly like the screen, only finer. Safe to nest here because the
    // detail lock removed every ["zoom"] expression from the style.
    const SC = (opts && opts.scale) || 1;
    const wS = (a) => (SC === 1 ? a : ["*", SC, a]);

    // EXACT ROAD WIDTHS. A palette can pin the per-class widths it was authored
    // with (mapbuilder's "Width" is the casing/stroke width; the white fill sits
    // inside it, which is what makes the outline read). Without this the shared
    // zoom curve decides, and a style copied from exact hex + width values comes
    // out with the wrong weights.
    // OUTER-ZOOM TAPER. Detail is otherwise frozen, but on the 5 widest views the
    // three biggest road classes (highway, arterial, minor) read too heavy, so they
    // thin out towards the outermost zoom and reach full authored weight from z13
    // in. Only these classes taper — service/paths and every other layer stay
    // locked. The interpolate must be the WHOLE property value with ["zoom"] as its
    // direct input (a nested ["zoom"] is illegal and blanks the map), so the taper
    // is applied OUTSIDE wS() and its outputs carry the print scale.
    // BIG_CUT applies to the three heaviest classes; ALL_CUT thins every other road
    // (service, tracks, etc.) by a gentler amount, so the whole network scales down
    // proportionally at the widest views instead of only the arterials.
    const TAPER_OUT = 11, TAPER_IN = 13, BIG_CUT = 0.45, ALL_CUT = 0.7;
    const bigOuterFactor = ["match", ["get", "class"],
      ["motorway", "trunk"], BIG_CUT,
      ["primary", "secondary", "tertiary"], BIG_CUT,
      ["minor", "residential", "unclassified", "living_street", "pedestrian"], BIG_CUT,
      ALL_CUT];
    const taperBig = (w) => ["interpolate", ["linear"], TZ,
      TAPER_OUT, ["*", bigOuterFactor, w], TAPER_IN, w];

    // OUTERMOST-ZOOM DECLUTTER. At the widest view building footprints and the
    // points-of-interest fills (park / school / medical / sports / other) turn into
    // noise, so they are hidden for that one zoom step and fade in from the next.
    // Applied as the WHOLE opacity property with ["zoom"] as its direct input (a
    // nested ["zoom"] is illegal and blanks the map).
    // The print/preview render from DEEPER tiles than the screen, so this must key
    // off the DISPLAY zoom (same reference the road taper uses) — on raw ["zoom"]
    // an export would print buildings/POI at full opacity while the screen hid them.
    const TZ = (opts && opts.taperZoom != null) ? opts.taperZoom : ["zoom"];
    // Buildings / POI areas are OFF until the display zoom where they are actually
    // legible on screen, then fade in over a short ramp. Below that they were a
    // preview-vs-print trap: a 0.6px outline anti-aliases to nothing at 1× on screen
    // but the print renders the same style at ~5×, where it becomes a solid stroke —
    // so buildings "appeared" only in the export. Keyed to TZ (display zoom) so the
    // screen, the WYSIWYG preview and the printed file all agree, in every style.
    const OUTER_Z = 12.6, OUTER_IN = 13.2;
    const hideOuter = (op) => ["interpolate", ["linear"], TZ, OUTER_Z, 0, OUTER_IN, op];
    // Buildings are switched OFF outright below the threshold rather than faded in:
    // a part-opacity footprint layer reads as a grey haze over the streets, which
    // looks worse than simply not drawing it. A step keeps the edge crisp.
    // Same, but also eased back at the closest zooms so school / medical / sports
    // blocks do not compete with the street network once every footprint shows.
    const hideOuterClose = (op) => ["interpolate", ["linear"], TZ, OUTER_Z, 0, OUTER_IN, op, 14, op * 0.7];

    // Water label ink: the palette's own label/text colour, tinted a little towards
    // the water hue so it still reads as water. Deriving it from the WATER colour
    // (as a first attempt did) makes the text almost the same value as the fill it
    // sits on — ~1.3:1 contrast, unreadable at label sizes.
    const waterLabelInk = theme.mapLabelColor || theme.labelColor || theme.text || "#3a3226";
    const waterLabel = theme.waterLabelColor || mixHex(waterLabelInk, water, 0.92);

    const RW = theme.roadW || null;
    const FILL_INSET = 1.2; // px of casing visible either side of a white road
    const rwCasing = RW ? ["match", ["get", "class"],
      ["primary", "secondary", "tertiary"], RW.arterial,
      ["service"], RW.service,
      ["living_street", "pedestrian"], RW.streetLimited != null ? RW.streetLimited : RW.minor,
      RW.minor] : null;
    const rwFill = RW ? ["match", ["get", "class"],
      ["motorway", "trunk"], RW.highway,
      ["primary", "secondary", "tertiary"], Math.max(0.5, RW.arterial - FILL_INSET),
      ["service"], Math.max(0.4, RW.service - 0.4),
      Math.max(0.5, RW.minor - FILL_INSET)] : null;

    const layers = [
      { id: "bg", type: "background", paint: { "background-color": bg } },
      { id: "landcover", type: "fill", source: "omt", "source-layer": "landcover",
        paint: { "fill-color": park, "fill-opacity": naturalOp } },
      // zoned land-use blocks — give texture when zoomed out; fade away once real
      // building footprints render (z13-14) so close-ups stay clean.
      { id: "landuse", type: "fill", source: "omt", "source-layer": "landuse",
        filter: ["in", ["get", "class"], ["literal", ["residential", "suburb", "neighbourhood", "quarter", "commercial", "retail", "industrial", "railway"]]],
        paint: { "fill-color": building,
          "fill-opacity": ["interpolate", ["linear"], ZIN, 9, 0.5, 12, 0.4, 13.5, 0] } },
      { id: "park", type: "fill", source: "omt", "source-layer": "park",
        paint: { "fill-color": park, "fill-opacity": theme.parkOpacity != null ? theme.parkOpacity : 0.7 } },
      // Points of interest (school / medical / sports complex / other civic land).
      // Same outermost-zoom declutter as buildings.
      { id: "poi-areas", type: "fill", source: "omt", "source-layer": "landuse",
        filter: ["in", ["get", "class"], ["literal", ["school", "university", "hospital", "stadium", "pitch", "cemetery", "military"]]],
        paint: { "fill-color": park, "fill-opacity": hideOuterClose((theme.parkOpacity != null ? theme.parkOpacity : 0.7) * 0.8) } },
      { id: "water", type: "fill", source: "omt", "source-layer": "water",
        paint: { "fill-color": water } },
      // Waterways drawn as LINES (streams, torrents, canalised rivers). Kept thin so
      // they read as watercourses rather than competing with the arterial roads —
      // water POLYGONS (sea, wide river mouths, docks) are unaffected.
      // Only real RIVERS are drawn as lines. Streams (the dry hill torrents that
      // sprawl over a city's backdrop) and engineered channels (canal / drain /
      // ditch) are hidden. Water POLYGONS — sea, docks, lakes, wide river mouths —
      // are a separate layer and unaffected.
      { id: "waterway", type: "line", source: "omt", "source-layer": "waterway",
        filter: ["!", ["in", ["get", "class"], ["literal", ["canal", "drain", "ditch", "stream"]]]],
        paint: { "line-color": water, "line-width": wS(["interpolate", ["linear"], ZIN, 11, 0.25, 16, 1.2]) } },
      { id: "building", type: "fill", source: "omt", "source-layer": "building",
        minzoom: 9, layout: { visibility: "visible" },
        paint: { "fill-color": building, "fill-opacity": ["step", TZ, 0, OUTER_IN, 0.9] } },
      // rail
      // Rail, minus TUNNELS: tile rail geometry runs straight through underground
      // sections, so metro/commuter tunnels drew as long lines "under" the city.
      { id: "rail", type: "line", source: "omt", "source-layer": "transportation",
        filter: ["all", ["==", ["get", "class"], "rail"], ["!=", ["get", "brunnel"], "tunnel"]], minzoom: 0,
        paint: { "line-color": rail, "line-width": wS(["interpolate", ["linear"], ZIN, 12, 0.4, 16, 1.6]) } },
    ];

    // Road casing: a WIDER line drawn UNDERNEATH the fill (the original look).
    const totalW = rwCasing ? taperBig(wS(rwCasing)) : taperBig(wS(["interpolate", ["linear"], ZIN,
      10, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 1.6, ["service"], 0.9, 1.2],
      12, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 2.5, ["service"], 1.1, 1.6],
      14, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 3.4, ["service"], 1.4, 2.2],
      17, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 7.5, ["service"], 2.2, 4.5]]));
    // minor footpaths / tracks — very thin. Some palettes hide them entirely
    // (theme.hidePaths), matching a style where Path/Others are switched off.
    if (!theme.hidePaths) layers.push({
      id: "road-path", type: "line", source: "omt", "source-layer": "transportation",
      filter: ["in", ["get", "class"], ["literal", ["path", "track", "footway", "cycleway", "pedestrian"]]],
      minzoom: 0, layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": road, "line-opacity": 0.85,
        "line-width": wS(["interpolate", ["linear"], ZIN, 13, 0.4, 17, 2]) },
    });
    // main road network — white
    const hwyTotal = wS(["interpolate", ["linear"], ZIN, 10, 1.3, 12, 2.0, 14, 2.8, 17, 6]);
    if (roadWhite) layers.push({
      id: "road-casing", type: "line", source: "omt", "source-layer": "transportation",
      filter: ["!", ["in", ["get", "class"], ["literal", ["rail", "ferry", "path", "track", "motorway", "trunk"]]]],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": casing, "line-width": totalW },
    });
    if (hwyCasing) layers.push({
      id: "highway-casing", type: "line", source: "omt", "source-layer": "transportation",
      filter: ["in", ["get", "class"], ["literal", ["motorway", "trunk"]]],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": hwyCasing, "line-width": hwyTotal },
    });
    const fillW = rwFill ? taperBig(wS(rwFill)) : taperBig(wS(["interpolate", ["linear"], ZIN,
      9, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 0.8, ["motorway", "trunk"], 0.6, ["service"], 0.3, 0.5],
      10, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 1.0, ["motorway", "trunk"], 0.7, ["service"], 0.3, 0.6],
      12, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 1.7, ["motorway", "trunk"], 1.2, ["service"], 0.4, 0.9],
      14, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 2.6, ["motorway", "trunk"], 1.9, ["service"], 0.6, 1.4],
      17, ["match", ["get", "class"], ["primary", "secondary", "tertiary"], 6, ["motorway", "trunk"], 4.5, ["service"], 1.4, 3.2]]));
    layers.push({
      id: "road", type: "line", source: "omt", "source-layer": "transportation",
      filter: ["!", ["in", ["get", "class"], ["literal", ["rail", "ferry", "path", "track", "footway", "cycleway"]]]],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": ["match", ["get", "class"], ["motorway", "trunk"], hwyC, road],
        "line-width": fillW },
    });


    // BUILDING OUTLINE, drawn AFTER every road layer so the stroke sits on top of
    // the roads instead of being covered by them. The fill stays below the roads;
    // only the outline is lifted. Width/opacity overridable per palette
    // (buildingStrokeW / buildingStrokeOpacity) so a style authored at "1px @ 100%"
    // gets exactly that.
    // Close-up zooms show far more footprints, so a full-weight outline reads as a
    // heavy grid. The stroke thins towards the closest zoom. SC is applied to the
    // NUMERIC stops (not via wS) because wrapping a zoom interpolate in ["*"] is
    // illegal in MapLibre and would blank the style.
    const bsW = (theme.buildingStrokeW != null ? theme.buildingStrokeW : 0.6) * SC;
    const CLOSE_FROM = 13.2, CLOSE_TO = 14, CLOSE_CUT = 0.6;
    if (buildingStroke) layers.push({
      id: "building-outline", type: "line", source: "omt", "source-layer": "building",
      minzoom: 0,
      paint: { "line-color": buildingStroke,
        "line-width": ["interpolate", ["linear"], TZ, CLOSE_FROM, bsW, CLOSE_TO, bsW * CLOSE_CUT],
        "line-opacity": ["step", TZ, 0, OUTER_IN, theme.buildingStrokeOpacity != null ? theme.buildingStrokeOpacity : 0.8] },
    });

    // River / lake / bay names, in the water colour so they read as water rather
    // than as another road label. Line placement follows a river's course; polygon
    // features (bays, lakes, docks) get a single centred label.
    if (opts && opts.labels) {
      layers.push({
        id: "water-label-line", type: "symbol", source: "omt", "source-layer": "waterway",
        filter: ["all", ["has", "name"], ["!", ["in", ["get", "class"], ["literal", ["canal", "drain", "ditch", "stream"]]]]],
        layout: { "symbol-placement": "line", "text-field": ["coalesce", ["get", "name:latin"], ["get", "name"]],
          "text-font": ["Noto Sans Italic"], "text-letter-spacing": 0.04, "symbol-spacing": 400,
          "text-size": wS(["interpolate", ["linear"], ZIN, 11, 8.5, 14, 10.5, 16, 12]) },
        paint: { "text-color": waterLabel, "text-halo-color": theme.bg, "text-halo-width": wS(1.1), "text-halo-blur": wS(0.4) },
      });
      // Inland water only — oceans/seas/bays/straits already have their own
      // `water-label` layer further down; matching them here would emit every
      // coastal name twice in a conflicting colour and font.
      layers.push({
        id: "water-label-area", type: "symbol", source: "omt", "source-layer": "water_name",
        filter: ["all", ["has", "name"],
          // oceans/seas/bays/straits have their own layer below; fountains and pools
          // are furniture, not places, and clutter a city centre with tiny names.
          ["!", ["in", ["get", "class"], ["literal", ["ocean", "sea", "bay", "strait", "fountain", "swimming_pool"]]]]],
        layout: { "text-field": ["coalesce", ["get", "name:latin"], ["get", "name"]],
          "text-font": ["Noto Sans Italic"], "text-letter-spacing": 0.06, "text-max-width": 7,
          "text-size": wS(["interpolate", ["linear"], ZIN, 11, 9.5, 14, 11.5, 16, 13]) },
        paint: { "text-color": waterLabel, "text-halo-color": theme.bg, "text-halo-width": wS(1.1), "text-halo-blur": wS(0.4) },
      });
    }

    // Optional MAIN labels only (place names + major road names) — kept sparse so
    // the poster never looks busy. Toggled by the customer.
    if (labelsOn) {
      // all named roads + street names (residential, service, etc. included so a
      // labelled poster actually shows the street network, not just arterials)
      layers.push({
        id: "road-label", type: "symbol", source: "omt", "source-layer": "transportation_name",
        filter: ["has", "name"],
        minzoom: 10,
        layout: {
          "symbol-placement": "line", "text-field": ["coalesce", ["get", "name:latin"], ["get", "name"]],
          "text-font": ["Noto Sans Regular"], "text-letter-spacing": 0.01, "symbol-spacing": 140,
          "text-size": wS(["interpolate", ["linear"], ZIN,
            10, ["match", ["get", "class"], ["motorway", "trunk", "primary"], 9, ["secondary", "tertiary"], 8, 7],
            13, ["match", ["get", "class"], ["motorway", "trunk", "primary"], 11.5, ["secondary", "tertiary"], 10, 9],
            16, ["match", ["get", "class"], ["motorway", "trunk", "primary"], 13.5, ["secondary", "tertiary"], 12, 11]]),
        },
        // halo defaults to the land colour; a palette can pin its authored halo.
        paint: { "text-color": labelColor, "text-halo-color": theme.labelHalo || bg, "text-halo-width": wS(1.6) },
      });
      // coastal / water names
      layers.push({
        id: "water-label", type: "symbol", source: "omt", "source-layer": "water_name",
        filter: ["in", ["get", "class"], ["literal", ["ocean", "sea", "bay", "strait"]]],
        layout: {
          "text-field": ["coalesce", ["get", "name:latin"], ["get", "name"]],
          "text-font": ["Noto Sans Regular"], "text-max-width": 6, "text-letter-spacing": 0.08,
          "text-size": wS(["interpolate", ["linear"], ZIN, 8, 10, 14, 13]),
        },
        paint: { "text-color": theme.waterLabelColor || mix(labelColor, water, 0.4), "text-halo-color": theme.labelHalo || bg, "text-halo-width": wS(1.4) },
      });
    }

    return {
      version: 8,
      glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
      sources: { omt: { type: "vector", url: OMT_URL } },
      layers,
    };
  }

  // ---- projection (MapLibre 512-tile mercator) for placing the print marker ----
  const mercX = (lng) => (lng + 180) / 360;
  const mercY = (lat) => {
    const s = Math.sin(lat * Math.PI / 180);
    return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
  };
  function project(lng, lat, center, zoom, W, H) {
    const ws = 512 * Math.pow(2, zoom);
    return {
      x: W / 2 + (mercX(lng) - mercX(center.lng)) * ws,
      y: H / 2 + (mercY(lat) - mercY(center.lat)) * ws,
    };
  }

  // Build a DOM element for the draggable live marker by reusing the canvas
  // drawMarker() so the on-screen pin matches the printed one exactly.
  function makeMarkerEl(style, theme, scale) {
    scale = scale || 1;
    const S = 3, w = Math.ceil(84 * scale), h = Math.ceil(96 * scale);  // logical box (pin is tallest)
    const cvs = document.createElement("canvas");
    cvs.width = w * S; cvs.height = h * S;
    cvs.style.width = w + "px"; cvs.style.height = h + "px";
    cvs.style.cursor = "grab"; cvs.style.display = "block";
    const ctx = cvs.getContext("2d"); ctx.scale(S, S);
    const anchorY = style === "pin" ? h - 6 * scale : h / 2;
    ctx.translate(w / 2, anchorY); ctx.scale(0.62 * scale, 0.62 * scale);
    if (window.drawMarker) window.drawMarker(ctx, style, theme, 0, 0);
    return { el: cvs, anchor: style === "pin" ? "bottom" : "center" };
  }

  // Largest texture / renderbuffer this GPU can allocate (16384 on desktop,
  // often 8192 on older integrated and mobile GPUs). Probed once and cached.
  var _glMax = 0;
  function glMaxPx() {
    if (_glMax) return _glMax;
    var lim = 8192;
    try {
      var c = document.createElement("canvas");
      var gl = c.getContext("webgl2") || c.getContext("webgl") || c.getContext("experimental-webgl");
      if (gl) lim = Math.max(2048, Math.min(gl.getParameter(gl.MAX_TEXTURE_SIZE), gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)));
    } catch (e) {}
    _glMax = lim; return lim;
  }

  // Blend two hex colours; t = how much of `a` to keep.
  function mixHex(a, b, t) {
    try {
      var pa = a.replace("#", ""), pb = b.replace("#", "");
      if (pa.length === 3) pa = pa[0]+pa[0]+pa[1]+pa[1]+pa[2]+pa[2];
      if (pb.length === 3) pb = pb[0]+pb[0]+pb[1]+pb[1]+pb[2]+pb[2];
      var out = "#";
      for (var i = 0; i < 3; i++) {
        var va = parseInt(pa.substr(i * 2, 2), 16), vb = parseInt(pb.substr(i * 2, 2), 16);
        var v = Math.round(va * t + vb * (1 - t));
        out += ("0" + Math.max(0, Math.min(255, v)).toString(16)).slice(-2);
      }
      return out;
    } catch (e) { return a; }
  }

  // Wait until a map has rendered its tiles (or a timeout).
  function mapReady(map, ms) {
    return new Promise((res) => {
      let done = false;
      const finish = () => { if (!done) { done = true; res(); } };
      const check = () => { if (map.loaded() && map.areTilesLoaded()) finish(); };
      map.on("idle", finish);
      map.on("render", check);
      check();
      setTimeout(finish, ms || 8000);
    });
  }

  // Off-screen high-resolution render of the poster MAP AREA (no marker — the
  // caller draws that on top so it stays crisp). Returns a W×H canvas.
  async function renderMapImage(opts) {
    const { theme, lng, lat, zoom, W, H } = opts;
    // The caller passes the PRINT pixel size (W×H) plus zoomOffset = the number of
    // zoom levels the print is bigger than the live preview. We render at print
    // pixels with the camera at that BOOSTED zoom, so OpenMapTiles serves its
    // deeper tiles — the full residential street grid and building footprints the
    // shallower tiles omit. Extent is unchanged (bigger container + higher zoom
    // cancel out), so the caller's marker projection still lines up, and stroke
    // weights are scaled to match, giving a poster that looks like the screen but
    // carries genuinely finer detail.
    const zoff = (opts && opts.zoomOffset) || 0;
    // Never ask for a buffer the GPU cannot allocate — that returns an EMPTY
    // canvas (the old "export comes out blank" bug) and no retry can rescue it,
    // since the limit is hardware. Probe the real ceiling once and clamp to it.
    const CAP = Math.min((typeof window !== "undefined" && window.SOUVENIR_GL_RENDERPX) || 10000, glMaxPx());
    // A buffer can also fail on AREA (memory) long before it hits the long-edge
    // limit — a 7000×10000 surface is ~70M px and simply won't allocate on most
    // GPUs, which returns a blank canvas. Budget total device pixels too.
    const AREAMAX = (typeof window !== "undefined" && window.SOUVENIR_GL_MAXPIXELS) || 26e6;
    // If the print exceeds either ceiling, shrink the render and drop the camera
    // zoom by the same factor so the framing is still identical.
    const kEdge = Math.min(1, CAP / Math.max(W, H));
    const kArea = Math.min(1, Math.sqrt(AREAMAX / (W * H)));
    const k = Math.min(kEdge, kArea);
    const rW = Math.max(1, Math.round(W * k)), rH = Math.max(1, Math.round(H * k));
    const camZoom = zoom + Math.log2(k);
    const scale = Math.pow(2, zoff) * k;
    // These are already true print pixels (300 DPI), so supersampling buys nothing
    // and costs the whole memory budget. Only oversample genuinely small renders
    // (the on-screen preview), and stay inside BOTH the edge and area budgets.
    const headroom = Math.min(CAP / Math.max(rW, rH), Math.sqrt(AREAMAX / (rW * rH)));
    const P = Math.max(1, Math.min(Math.max(rW, rH) >= 1600 ? 1 : 2, headroom));
    const holder = document.createElement("div");
    holder.style.cssText = "position:absolute;left:-99999px;top:0;width:" + rW + "px;height:" + rH + "px;";
    document.body.appendChild(holder);
    let out = null;
    try {
      const m = new maplibregl.Map({
        container: holder, style: buildGLStyle(theme, { labels: opts.labels, scale: scale, taperZoom: zoom - zoff }), center: [lng, lat], zoom: camZoom,
        interactive: false, attributionControl: false, fadeDuration: 0, preserveDrawingBuffer: true, pixelRatio: P,
      });
      await mapReady(m, (opts && opts.readyMs) || 15000);
      await new Promise((r) => setTimeout(r, 60));
      let cv = m.getCanvas();
      // Guard against an EMPTY frame (tiles not painted yet on a slow connection,
      // or the GPU returned a blank buffer): if the canvas is a single flat colour,
      // wait and repaint a few times before giving up.
      const isBlank = (canvas) => {
        try {
          const t = document.createElement("canvas"); t.width = 24; t.height = 24;
          const tx = t.getContext("2d"); tx.drawImage(canvas, 0, 0, 24, 24);
          const dd = tx.getImageData(0, 0, 24, 24).data; const f = dd[0] + "," + dd[1] + "," + dd[2];
          for (let i = 4; i < dd.length; i += 4) { if (dd[i] + "," + dd[i + 1] + "," + dd[i + 2] !== f) return false; }
          return true;
        } catch (e) { return false; }
      };
      for (let tries = 0; tries < 5 && isBlank(cv); tries++) {
        await new Promise((r) => setTimeout(r, 1400));
        try { m.triggerRepaint && m.triggerRepaint(); } catch (e) {}
        cv = m.getCanvas();
      }
      out = document.createElement("canvas"); out.width = cv.width; out.height = cv.height;
      const octx = out.getContext("2d"); octx.imageSmoothingEnabled = true; octx.imageSmoothingQuality = "high";
      octx.drawImage(cv, 0, 0, out.width, out.height);
      m.remove();
    } catch (e) { /* leave null → caller paints paper bg */ }
    holder.remove();
    return out;
  }

  window.buildGLStyle = buildGLStyle;
  window.SouvenirGL = { mix, hx, project, makeMarkerEl, mapReady, renderMapImage, isGLTheme: (t) => !!t && !t.satellite && !t.vectorBlocks };
  window.SouvenirGLHelpers = { mix, hx };
})();
